import { lazy, ReactNode, Suspense, useContext, useState, type ComponentType } from 'react';

import { faArrowLeftLong, faCopy } from '@fortawesome/free-solid-svg-icons';
import { Breadcrumb, BreadcrumbItemProps, Card, Stack } from 'react-bootstrap';

import IconButton from '@components/Button/IconButton';
import { DraftOrderMethylationReanalysisChip } from '@components/DraftOrder/DraftOrderMethylationReanalysisChip';
import { DraftOrderReanalysisChip } from '@components/DraftOrder/DraftOrderReanalysisChip';
import CancelConfirmDialog from '@components/RequisitionCreate/CancelConfirmDialog/CancelConfirmDialog';
import useDraftOrderDetail from '@contexts/useDraftOrderDetail';
import { RerequisitionType } from '@customTypes/requisition';
import { isMethylationReflexDraft, isReanalysisDraft } from '@helpers/draftOrder';
import { Context } from '@store/store';
import { UI_BUTTON_VAR } from '@constants/constants';

import type { SaveDraftOrderDialogProps } from '../SaveDraftOrderDialog/SaveDraftOrderDialog';

import './OrderFormContainer.scss';

interface IProps {
  breadcrumbs: BreadcrumbItemProps[];
  title: string;
  subtitle?: string;
  children: ReactNode;
  onCancel: () => void;
  /** Cancel dialog returns to patient detail instead of orders list (patient-scoped flows). */
  cancelReturnsToPatient?: boolean;
  /** Show Save as Draft when form has at least one clinician. Defaults to true. */
  enableSaveAsDraft?: boolean;
  rerequisitionFrom?: string;
  rerequisitionType?: RerequisitionType;
}

const LazySaveDraftOrderDialog = lazy(
  () => import('../SaveDraftOrderDialog/SaveDraftOrderDialog'),
) as ComponentType<SaveDraftOrderDialogProps>;

const DEFAULT_SUBTITLE =
  'Fill out the form in each step as provided. Use the Next button to continue to the next step, ' +
  'and the Submit button when all steps are completed. Some product selections may not require additional information.';

export default function OrderFormContainer({
  breadcrumbs,
  children,
  title,
  subtitle,
  onCancel,
  cancelReturnsToPatient = false,
  enableSaveAsDraft = true,
  rerequisitionFrom,
  rerequisitionType,
}: IProps) {
  const [state] = useContext(Context);
  const { draftOrder } = useDraftOrderDetail();

  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [showDraftOrderDialog, setShowDraftOrderDialog] = useState<boolean>(false);

  const showSaveAsDraft = enableSaveAsDraft && !!state.requisitionTaskForm?.formdata?.clinicians?.length;

  const showReanalysisMarker = !!draftOrder && isReanalysisDraft(draftOrder);
  const showMethylationMarker = !!draftOrder && isMethylationReflexDraft(draftOrder);
  const showRerequisitionMarker = showReanalysisMarker || showMethylationMarker;
  const cardHeaderClassName = showRerequisitionMarker
    ? 'order-form-container__card-header order-form-container__card-header--with-marker'
    : 'order-form-container__card-header';

  return (
    <Suspense>
      <Stack gap={4} className='layout-single-col-py order-form-container' data-testid='newOrderPage'>
        {breadcrumbs.length > 0 && (
          <Breadcrumb listProps={{ className: 'mb-0' }}>
            {breadcrumbs.map((breadcrumb, index) => (
              <Breadcrumb.Item key={index} {...breadcrumb} />
            ))}
          </Breadcrumb>
        )}
        {/* TODO: refactor to not use card components */}
        <Card as='section' className='px-4 pb-4 pt-2 border-0 box-shadow-50 position-static'>
          <div className={cardHeaderClassName}>
            {showReanalysisMarker && (
              <div className='order-form-container__reanalysis-marker'>
                <DraftOrderReanalysisChip />
              </div>
            )}
            {showMethylationMarker && (
              <div className='order-form-container__reanalysis-marker'>
                <DraftOrderMethylationReanalysisChip />
              </div>
            )}
            <Card.Title as='h1' className='mt-md-1 page-title'>
              {title}
            </Card.Title>
            <Card.Subtitle className='mb-0 mt-md-1 flex flex-column text-secondary page-subtitle' as='p'>
              {!!draftOrder && <span className='fw-bold fs-6 d-block mb-4'>Edit or place your draft order.</span>}
              <span>{subtitle || DEFAULT_SUBTITLE}</span>
            </Card.Subtitle>
          </div>
          <Card.Body className='p-0 pt-4' data-openreplay-sanitize>
            {children}
          </Card.Body>
          <Card.Footer className='d-flex flex-column flex-sm-row gap-4 gap-sm-3 bg-white p-0 pt-4 mt-3 border-top'>
            <IconButton
              variant={UI_BUTTON_VAR}
              className='justify-content-center justify-content-sm-start'
              startIcon={faArrowLeftLong}
              onClick={() => setShowConfirmDialog(true)}
              data-testid='cancelAndReturnBtn'
            >
              Cancel and return
            </IconButton>
            {showSaveAsDraft && (
              <IconButton
                variant={UI_BUTTON_VAR}
                className='justify-content-center justify-content-sm-start'
                startIcon={faCopy}
                onClick={() => setShowDraftOrderDialog(true)}
              >
                Save as Draft
              </IconButton>
            )}
          </Card.Footer>
        </Card>
      </Stack>

      {showConfirmDialog && (
        <CancelConfirmDialog
          onClose={() => setShowConfirmDialog(false)}
          onConfirm={onCancel}
          cancelReturnsToPatient={cancelReturnsToPatient}
        />
      )}
      {showDraftOrderDialog && (
        <LazySaveDraftOrderDialog
          onClose={() => setShowDraftOrderDialog(false)}
          rerequisitionFrom={rerequisitionFrom}
          rerequisitionType={rerequisitionType}
        />
      )}
    </Suspense>
  );
}
